/**
 * Mention Portal 下拉定位（Skill button/slash、KB @ 共用）
 */
import { useState, useCallback, useEffect, RefObject } from 'react';
import { useEventListener } from 'ahooks';
import { getVirtualAnchorRect } from './caretUtils';

const DROPDOWN_GAP = 4;
const DROPDOWN_VIEWPORT_GAP = 8;
const DEFAULT_ESTIMATED_WIDTH = 420;
/** 容器布局下拉相对输入框左右各内缩 */
const CONTAINER_HORIZONTAL_INSET = 10;

export type TMentionDropdownPos = {
  top?: number;
  bottom?: number;
  left: number;
  width?: number;
} | null;

export type TMentionDropdownLayout = 'trigger' | 'container';

export interface IUseMentionDropdownPositionOptions {
  enabled: boolean;
  isDropDown: boolean;
  layout: TMentionDropdownLayout;
  forceTriggerLayoutRef?: RefObject<boolean>;
  dropdownRef: RefObject<HTMLElement | null>;
  triggerRef?: RefObject<HTMLElement | null>;
  inputBoxContainerRef?: RefObject<HTMLElement | null>;
  mentionAnchorRef?: RefObject<HTMLElement | null>;
  estimatedWidth?: number;
  /** 变化时强制重算位置（如 @ 后 query / 锚点更新） */
  recalcToken?: string | number;
}

function clampDropdownLeft(rawLeft: number, dropdownWidth: number): number {
  const viewportWidth = window.innerWidth;
  const maxLeft = Math.max(
    viewportWidth - dropdownWidth - DROPDOWN_VIEWPORT_GAP,
    DROPDOWN_VIEWPORT_GAP,
  );
  return Math.min(Math.max(rawLeft, DROPDOWN_VIEWPORT_GAP), maxLeft);
}

export type TMentionDropdownPositionResult = {
  dropdownPos: TMentionDropdownPos;
};

export function useMentionDropdownPosition({
  enabled,
  isDropDown,
  layout,
  forceTriggerLayoutRef,
  dropdownRef,
  triggerRef,
  inputBoxContainerRef,
  mentionAnchorRef,
  estimatedWidth = DEFAULT_ESTIMATED_WIDTH,
  recalcToken,
}: IUseMentionDropdownPositionOptions): TMentionDropdownPositionResult {
  const [dropdownPos, setDropdownPos] = useState<TMentionDropdownPos>(null);

  const updateDropdownPos = useCallback(() => {
    const useTrigger =
      forceTriggerLayoutRef?.current === true || layout === 'trigger';

    if (useTrigger) {
      const triggerEl = triggerRef?.current;
      if (!triggerEl) {
        setDropdownPos(null);
        return;
      }
      const dropdownWidth = dropdownRef.current?.offsetWidth || estimatedWidth;
      const triggerRect = triggerEl.getBoundingClientRect();
      const clampedLeft = clampDropdownLeft(triggerRect.left, dropdownWidth);

      if (isDropDown) {
        setDropdownPos({
          top: triggerRect.bottom + DROPDOWN_GAP,
          left: clampedLeft,
        });
      } else {
        setDropdownPos({
          bottom: window.innerHeight - triggerRect.top + DROPDOWN_GAP,
          left: clampedLeft,
        });
      }
      return;
    }

    const containerEl = inputBoxContainerRef?.current;
    if (!containerEl) {
      setDropdownPos(null);
      return;
    }
    const containerRect = containerEl.getBoundingClientRect();
    const horizontalInset = CONTAINER_HORIZONTAL_INSET;
    const dropdownWidth = Math.max(
      0,
      containerRect.width - horizontalInset * 2,
    );
    const clampedLeft = clampDropdownLeft(
      containerRect.left + horizontalInset,
      dropdownWidth,
    );
    const anchorRect = getVirtualAnchorRect(
      mentionAnchorRef?.current,
      containerRect,
    );

    if (isDropDown) {
      setDropdownPos({
        top:
          (anchorRect ? anchorRect.bottom : containerRect.bottom) +
          DROPDOWN_GAP,
        left: clampedLeft,
        width: dropdownWidth,
      });
    } else {
      const bottomOffset = window.innerHeight - containerRect.top + DROPDOWN_GAP;
      setDropdownPos({
        bottom: anchorRect
          ? window.innerHeight - anchorRect.top + DROPDOWN_GAP
          : bottomOffset,
        left: clampedLeft,
        width: dropdownWidth,
      });
    }
  }, [
    layout,
    forceTriggerLayoutRef,
    isDropDown,
    dropdownRef,
    triggerRef,
    inputBoxContainerRef,
    mentionAnchorRef,
    estimatedWidth,
  ]);

  useEffect(() => {
    if (!enabled) {
      setDropdownPos(null);
      return;
    }
    updateDropdownPos();
    const rafId = requestAnimationFrame(() => {
      updateDropdownPos();
    });
    return () => cancelAnimationFrame(rafId);
  }, [enabled, updateDropdownPos, recalcToken]);

  useEffect(() => {
    if (!enabled) return;
    const dropdownEl = dropdownRef.current;
    if (!dropdownEl || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => {
      updateDropdownPos();
    });
    observer.observe(dropdownEl);
    return () => observer.disconnect();
  }, [enabled, dropdownRef, updateDropdownPos]);

  useEventListener('resize', () => {
    if (enabled) updateDropdownPos();
  });

  return { dropdownPos };
}
